/**
 * Unread and total counts of the current user's inbox, kept fresh by polling while the tab is
 * visible and by a refetch when it becomes visible again — there is no subscription for the inbox.
 */
import { useQuery } from '@apollo/client/react';
import { useEffect } from 'react';
import { InboxSummaryDocument } from '@/graphql';

/** How often the menu entry re-reads the counts while the tab stays open. */
export const INBOX_SUMMARY_POLL_MS = 60_000;

export interface InboxSummary {
  unreadCount: number;
  totalCount: number;
}

interface UseInboxSummaryOptions {
  /** False for a user who can never receive an inbox item; nothing is queried then. */
  enabled: boolean;
}

const EMPTY_SUMMARY: InboxSummary = { unreadCount: 0, totalCount: 0 };

export function useInboxSummary({ enabled }: UseInboxSummaryOptions): InboxSummary {
  const { data, refetch } = useQuery(InboxSummaryDocument, {
    skip: !enabled,
    pollInterval: INBOX_SUMMARY_POLL_MS,
    // A hidden tab is not looked at; the visibility refetch below catches up once it is.
    skipPollAttempt: () => document.visibilityState === 'hidden',
    fetchPolicy: 'cache-and-network'
  });

  useEffect(() => {
    if (!enabled) return;
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      // A failed refresh keeps the last counts; the next poll tries again.
      refetch().catch(() => undefined);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [enabled, refetch]);

  if (!enabled || !data) return EMPTY_SUMMARY;
  return {
    unreadCount: data.inboxSummary.unreadCount,
    totalCount: data.inboxSummary.totalCount
  };
}
